import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import Button from '../common/Button';

/**
 * Modal component for cropping and adjusting images
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.image - Image URL or data URL to crop
 * @param {Function} props.onSave - Function to call with the cropped image blob
 * @param {string} props.title - Modal title
 * @param {Object} props.aspectRatio - Aspect ratio for cropping (e.g., {width: 1, height: 1})
 * @param {Object} props.recommendedSize - Recommended image size (e.g., {width: 400, height: 400})
 * @returns {JSX.Element} ImageCropModal component
 */
const ImageCropModal = ({
  isOpen,
  onClose,
  image,
  onSave,
  title = 'Crop Image',
  aspectRatio = { width: 1, height: 1 },
  recommendedSize = { width: 400, height: 400 }
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const onCropComplete = useCallback((croppedArea, croppedAreaPixelsData) => {
    setCroppedAreaPixels(croppedAreaPixelsData);
  }, []);
  
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  // Create a canvas with the cropped image
  const createCroppedImage = async () => {
    try {
      setIsProcessing(true);
      
      const imgElement = new Image();
      imgElement.src = image; // Use the image URL directly from props
      
      await new Promise((resolve) => {
        imgElement.onload = resolve;
      });
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate the desired size based on the recommended size, but maintaining the aspect ratio
      const maxWidth = recommendedSize.width;
      const maxHeight = recommendedSize.height;
      let width = croppedAreaPixels.width;
      let height = croppedAreaPixels.height;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      // Set canvas size
      canvas.width = width;
      canvas.height = height;
      
      // Draw the cropped image onto the canvas with rotation
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(1, 1);
      
      ctx.drawImage(
        imgElement,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        -canvas.width / 2,
        -canvas.height / 2,
        canvas.width,
        canvas.height
      );
      
      ctx.restore();
      
      // Get the data URL from the canvas and convert to blob
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      const blob = await (await fetch(dataUrl)).blob();
      
      // Call the onSave function with the blob
      onSave(blob);
      onClose();
    } catch (error) {
      console.error('Error creating cropped image:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        
        <div className="px-6 py-4">
          <div className="mb-4 text-sm text-gray-600">
            Recommended size: {recommendedSize.width}px × {recommendedSize.height}px
          </div>
          
          {/* Crop container */}
          <div className="relative h-96 bg-gray-100 mb-4">
            <Cropper
              image={image}
              crop={crop}
              rotation={rotation}
              zoom={zoom}
              aspect={aspectRatio.width / aspectRatio.height}
              onCropChange={setCrop}
              onRotationChange={setRotation}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          
          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zoom
              </label>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rotation
              </label>
              <input
                type="range"
                value={rotation}
                min={0}
                max={360}
                step={1}
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
          <Button
            variant="light"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={createCroppedImage}
            isLoading={isProcessing}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

ImageCropModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  image: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string,
  aspectRatio: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  recommendedSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  })
};

export default ImageCropModal;